import {
    icon as renderIcon,
    parse,
    type AbstractElement,
    type IconDefinition,
    type Transform
} from "@fortawesome/fontawesome-svg-core";
import {
    FontAwesomeIcon,
    FontAwesomeLayers,
    type FontAwesomeIconProps,
    type FontAwesomeLayersProps
} from "@fortawesome/react-fontawesome";
import { createElement, useId, type CSSProperties, type ReactNode } from "react";
import classes from "./Icon.module.css";

export type AppIconLayer = Readonly<
    Omit<FontAwesomeIconProps, "icon" | "ref"> & {
        icon: IconDefinition;
        /** Clear underlying layers around this glyph, including its secondary path. */
        knockout?: boolean;
        /**
         * Where this layer sits once the icon is too small to carry the whole chain, or `false`
         * to drop it there. A composition with any of these renders both forms and lets a
         * container query pick, so the choice follows the icon's real size rather than a guess
         * about the font size at the call site.
         */
        compact?: string | Transform | false;
    }
>;

export type AppIconDefinition = IconDefinition | readonly AppIconLayer[];

export interface IconProps extends Omit<FontAwesomeIconProps, "icon" | "ref"> {
    icon: AppIconDefinition;
}

const containerPropertyNames = new Set([
    "accessKey",
    "contentEditable",
    "dir",
    "draggable",
    "hidden",
    "id",
    "lang",
    "role",
    "slot",
    "spellCheck",
    "tabIndex",
    "title"
]);

/**
 * Identifies attributes and event handlers that belong on the compound-icon container.
 *
 * Postconditions: Uses the explicit name set, aria/data prefixes and capitalized event pattern;
 * other properties remain shared layer props.
 */
const isContainerProperty = (name: string) =>
    containerPropertyNames.has(name) ||
    name.startsWith("aria-") ||
    name.startsWith("data-") ||
    /^on[A-Z]/.test(name);

// Font Awesome lays every glyph out on a 16-unit em and states its transforms in that grid.
// The composition adopts the same grid, so one em reads as one em exactly as it does for a
// plain icon, and so a silhouette can never drift away from the glyph it cuts out.
const emUnits = 16;

// Width of the clear ring a knockout layer burns into everything beneath it, in em units.
// Wider than this and a badge starts eating the glyph it sits on rather than parting from it.
const knockoutHalo = 1;

/**
 * Converts hyphen-lowercase pairs in attribute names to their React-style spelling.
 */
const camelCase = (name: string) =>
    name.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());

/**
 * Projects Font Awesome style declarations into React style properties.
 *
 * Assumptions: Only segments containing exactly one colon are retained. Names/values are trimmed,
 * values remain strings and later duplicate properties win. This is not a general CSS parser.
 */
const parseStyle = (declarations: string): CSSProperties =>
    Object.fromEntries(
        declarations
            .split(";")
            .map(declaration => declaration.split(":"))
            .filter(parts => parts.length === 2)
            .map(([name, value]) => [camelCase(name.trim()), value.trim()])
    );

// Font Awesome hands back an abstract tree rather than markup, so render it into the
// composition directly. Delegating to <FontAwesomeIcon> would nest an <svg> per layer, and a
// nested SVG carries its own viewport: browsers ignore CSS width/height on one, so the glyph
// silently resolves against the outer viewBox instead of the 16-unit box it was placed in.
/**
 * Converts a Font Awesome abstract element recursively without introducing a nested SVG viewport.
 *
 * Postconditions: Maps class/style and attribute names into React props while retaining data/aria
 * names and child order. Trusted Font Awesome nodes supply tags and attributes; errors propagate.
 */
const toReactNode = (node: AbstractElement, key: number): ReactNode => {
    const properties: Record<string, unknown> = { key };

    Object.entries(node.attributes ?? {}).forEach(([name, value]) => {
        if (name === "class") {
            properties.className = value;
        } else if (name === "style") {
            properties.style = parseStyle(String(value));
        } else if (name.startsWith("data-") || name.startsWith("aria-")) {
            properties[name] = value;
        } else {
            properties[camelCase(name)] = value;
        }
    }
    );

    return createElement(node.tag, properties, node.children?.map(toReactNode));
};

// Never copy opacity or duotone classes into a silhouette: it must fully occlude.
/**
 * Builds opaque black paths and transformed groups for knockout masking.
 *
 * Postconditions: Path data and transforms survive; opacity and duotone classes are omitted.
 * Assumptions: Input is a Font Awesome abstract tree and stroke width uses that glyph's units.
 */
const silhouette = (node: AbstractElement, strokeWidth: number, key: number): ReactNode => {
    if (node.tag === "path") {
        return (
            <path
                key={key}
                d={node.attributes.d}
                transform={node.attributes.transform}
                fill="black"
                stroke="black"
                strokeWidth={strokeWidth}
                strokeLinejoin="round"
            />
        );
    }
    return (
        <g
            key={key}
            transform={node.attributes?.transform}
        >
            {node.children?.map((child, index) => silhouette(child, strokeWidth, index))}
        </g>
    );
};

interface PreparedLayer {
    readonly knockout: boolean;
    readonly className?: string;
    readonly style?: CSSProperties;
    /** Identifies the layer the way Font Awesome labels a rendered icon. */
    readonly prefix: string;
    readonly iconName: string;
    /** Inherited by the glyph's currentColor fills, as it is on a plain icon. */
    readonly color?: string;
    /** Centres the icon's own artwork on the shared em grid. */
    readonly placement: string;
    /** Path units per em unit, so stroke widths can be stated in em units. */
    readonly unitScale: number;
    readonly content: readonly AbstractElement[];
}

/**
 * Resolves layer overrides and prepares glyph geometry on the shared em grid.
 *
 * Preconditions: The icon definition has valid nonzero dimensions and renderable abstract output.
 * Postconditions: Layer props override shared props; compact placement is used only when requested
 * and present. Returns geometry without mutating inputs; parse/render failures propagate.
 */
const prepare = (
    layer: AppIconLayer,
    shared: Omit<FontAwesomeIconProps, "icon">,
    useCompact = false
): PreparedLayer => {
    const { icon, transform, className, style, color, knockout, compact } = { ...shared, ...layer };
    const [width, height] = icon.icon;
    const placed = useCompact && compact !== undefined && compact !== false ? compact : transform;
    const parsed: Transform | undefined =
        typeof placed === "string" ? parse.transform(placed) : placed;
    const rendered = renderIcon(icon, { transform: parsed });
    return {
        knockout: knockout === true,
        className,
        style,
        prefix: icon.prefix,
        iconName: icon.iconName,
        color,
        placement: `scale(${emUnits / height}) translate(${-width / 2} ${-height / 2})`,
        unitScale: (emUnits / height) * (Math.abs(parsed?.size ?? emUnits) / emUnits),
        content: rendered.abstract[0].children ?? []
    };
};

// A mask hides everything outside its region, so give it room for whatever a transform pushes
// beyond the em.
const maskOrigin = -2 * emUnits;
const maskExtent = 4 * emUnits;

/**
 * Renders prepared layers and masks each earlier layer against later knockout silhouettes.
 *
 * Preconditions: The supplied ID namespace distinguishes this composition's mask references.
 * Postconditions: Preserves layer order and keeps mask groups separate from glyph placement;
 * the SVG is decorative, leaving accessible naming to the owning container.
 */
const Composition = ({
    layers,
    shared,
    id,
    compact,
    className
}: {
    layers: readonly AppIconLayer[];
    shared: Omit<FontAwesomeIconProps, "icon">;
    id: string;
    compact: boolean;
    className?: string;
}) => {
    const prepared = layers.map(layer => prepare(layer, shared, compact));

    /** Determines whether any later prepared layer must cut through this layer. */
    const isKnockedOut = (index: number) =>
        prepared.some((layer, laterIndex) => laterIndex > index && layer.knockout);

    return (
        // svg-inline--fa gives the composition the sizing, overflow and duotone rules Font
        // Awesome applies to a single icon; without it the secondary paths lose their opacity.
        <svg
            className={["svg-inline--fa", className].filter(Boolean).join(" ")}
            data-icon-composition={compact ? "compact" : "masked"}
            viewBox={`${-emUnits / 2} ${-emUnits / 2} ${emUnits} ${emUnits}`}
            aria-hidden="true"
            focusable="false"
        >
            <defs>
                {prepared.map((layer, index) =>
                    layer.knockout ? (
                        <g
                            key={index}
                            id={`${id}-knockout-${index}`}
                            transform={layer.placement}
                        >
                            {layer.content.map((node, nodeIndex) =>
                                silhouette(node, knockoutHalo / layer.unitScale, nodeIndex)
                            )}
                        </g>
                    ) : null
                )}
                {prepared.map((_, index) =>
                    isKnockedOut(index) ? (
                        <mask
                            key={index}
                            id={`${id}-layer-${index}`}
                            maskUnits="userSpaceOnUse"
                            x={maskOrigin}
                            y={maskOrigin}
                            width={maskExtent}
                            height={maskExtent}
                            style={{ maskType: "luminance" }}
                        >
                            <rect
                                x={maskOrigin}
                                y={maskOrigin}
                                width={maskExtent}
                                height={maskExtent}
                                fill="white"
                            />
                            {prepared.map((later, laterIndex) =>
                                laterIndex > index && later.knockout ? (
                                    <use
                                        key={laterIndex}
                                        href={`#${id}-knockout-${laterIndex}`}
                                    />
                                ) : null
                            )}
                        </mask>
                    ) : null
                )}
            </defs>
            {prepared.map((layer, index) => (
                // The mask stays on a group of its own: a transform on the masked element moves
                // the mask with it, and the silhouettes are already placed on the em grid.
                <g
                    key={index}
                    mask={isKnockedOut(index) ? `url(#${id}-layer-${index})` : undefined}
                    data-prefix={layer.prefix}
                    data-icon={layer.iconName}
                    color={layer.color}
                    className={layer.className}
                    style={layer.style}
                    aria-hidden="true"
                >
                    <g transform={layer.placement}>{layer.content.map(toReactNode)}</g>
                </g>
            )
            )}
        </svg>
    );
};

/**
 * Allocates mask IDs and chooses full-only or paired full/compact compositions.
 *
 * Postconditions: Paired variants render only when at least one layer has compact false.
 * Otherwise only the full variant renders, even if compact transforms exist. CSS selects between
 * paired variants; this component does not measure the container.
 */
const MaskedLayers = ({
    layers,
    shared
}: {
    layers: readonly AppIconLayer[];
    shared: Omit<FontAwesomeIconProps, "icon">;
}) => {
    const id = useId().replace(/[^A-Za-z0-9_-]/g, "");
    const compactLayers = layers.filter(layer => layer.compact !== false);
    // Only worth a second composition when the chain actually sheds something.
    if (compactLayers.length === layers.length) {
        return (
            <Composition
                layers={layers}
                shared={shared}
                id={id}
                compact={false}
            />
        );
    }
    return (
        <>
            <Composition
                layers={layers}
                shared={shared}
                id={`${id}-f`}
                compact={false}
                className={classes.full}
            />
            <Composition
                layers={compactLayers}
                shared={shared}
                id={`${id}-c`}
                compact
                className={classes.compact}
            />
        </>
    );
};

/**
 * Renders a plain icon or a compound icon with container-owned accessibility and interaction.
 *
 * Postconditions: Plain definitions delegate directly. Arrays split container and layer props;
 * any truthy knockout selects masking. Unmasked layers retain their original override order.
 * Absent accessible names default compound icons to aria-hidden; explicit caller attributes win.
 * Rendering produces elements without guaranteeing mounting or browser paint.
 */
const Icon = ({ icon, className, size, style, ...properties }: IconProps) => {
    if (!Array.isArray(icon)) {
        return (
            <FontAwesomeIcon
                icon={icon as IconDefinition}
                className={className}
                size={size}
                style={style}
                {...properties}
            />
        );
    }

    const containerProperties: Record<string, unknown> = {};
    const sharedLayerProperties: Record<string, unknown> = {};

    Object.entries(properties).forEach(([name, value]) => {
        if (isContainerProperty(name)) {
            containerProperties[name] = value;
        } else {
            sharedLayerProperties[name] = value;
        }
    }
    );

    const ariaHidden = containerProperties["aria-hidden"];
    const hasAccessibleName =
        containerProperties["aria-label"] !== undefined ||
        containerProperties["aria-labelledby"] !== undefined ||
        containerProperties.title !== undefined;

    if (ariaHidden === undefined) {
        containerProperties["aria-hidden"] = hasAccessibleName ? undefined : true;
    }
    if (containerProperties.role === undefined && (hasAccessibleName || ariaHidden === false)) {
        containerProperties.role = "img";
    }

    const layersClassName = [className, size ? `fa-${size}` : undefined, classes.container]
        .filter(Boolean)
        .join(" ");

    return (
        <FontAwesomeLayers
            {...(containerProperties as unknown as FontAwesomeLayersProps)}
            className={layersClassName}
            style={style as CSSProperties}
        >
            {icon.some(layer => layer.knockout) ? (
                <MaskedLayers
                    layers={icon}
                    shared={sharedLayerProperties as Omit<FontAwesomeIconProps, "icon">}
                />
            ) : (
                icon.map(({ knockout: _knockout, ...layer }, index) => (
                    <FontAwesomeIcon
                        {...(sharedLayerProperties as Omit<FontAwesomeIconProps, "icon">)}
                        {...layer}
                        aria-hidden
                        focusable={false}
                        icon={layer.icon}
                        key={`${layer.icon.prefix}-${layer.icon.iconName}-${index}`}
                    />
                )
                )
            )}
        </FontAwesomeLayers>
    );
};

export default Icon;
